/**
 * Coups de pouce — Attendu 1119 « Construire le symétrique d'un point par
 * demi-tour » (automatisme 4e).
 *
 * placer_* (placement interactif, QCM) : entrée PARTAGÉE — O milieu de
 * [MM'], le geste sur quadrillage (refaire de O le déplacement de M à O), la
 * vérification, le piège de la symétrie axiale. L'exemple : O à l'origine,
 * M 2 carreaux à gauche et 3 en haut → M' 2 à droite et 3 en bas, flèches de
 * déplacement dessinées en deux temps.
 *
 * vf_tableau_vf : méthode de vérification sur la figure — même déplacement de
 * M à O et de O à N, puis les trois contrôles (alignés, même distance, de
 * l'autre côté). L'exemple : O(1 ; 0), M(−2 ; 2), N(4 ; −2).
 *
 * rappel_tableau_vf : pur rappel, pas d'entrée (doctrine 2026-07-31).
 */

// ─── Quadrillage de −5 à 5 (16 px par carreau) ─────────────────────────────
const CELL = 16; const X0 = 10; const Y0 = 88;
const px = (x) => X0 + (x + 5) * CELL; const py = (y) => Y0 + (5 - y) * CELL;
const GRILLE = (() => {
    let g = `<rect x="${X0}" y="${Y0}" width="${10 * CELL}" height="${10 * CELL}" fill="#ffffff"/>`;
    for (let v = -5; v <= 5; v++) {
        g += `<line x1="${px(v)}" y1="${py(-5)}" x2="${px(v)}" y2="${py(5)}" stroke="#cbd5e1" stroke-width="1"/>`;
        g += `<line x1="${px(-5)}" y1="${py(v)}" x2="${px(5)}" y2="${py(v)}" stroke="#cbd5e1" stroke-width="1"/>`;
    }
    return g;
})();
const POINT = (x, y, label, couleur, dx = 6, dy = -6) => `<circle cx="${px(x)}" cy="${py(y)}" r="4" fill="${couleur}"/><text x="${px(x) + dx}" y="${py(y) + dy}" font-family="system-ui,sans-serif" font-size="12.5" font-weight="800" fill="${couleur}">${label}</text>`;
/** Chemin en L (horizontal puis vertical) de (x1,y1) à (x2,y2), avec pointe de flèche. */
const FLECHE = (x1, y1, x2, y2, couleur) => {
    const cx = px(x2); const cy = py(y1); const ex = px(x2); const ey = py(y2);
    const sens = y2 < y1 ? 1 : -1;
    return `<polyline points="${px(x1)},${py(y1)} ${cx},${cy} ${ex},${ey}" fill="none" stroke="${couleur}" stroke-width="2.4" stroke-linejoin="round" stroke-dasharray="6 3"/>`
        + `<polygon points="${ex},${ey} ${ex - 4},${ey - sens * 8} ${ex + 4},${ey - sens * 8}" fill="${couleur}"/>`;
};

// ─── Placer M' : O(0 ; 0), M(−2 ; 3) → M'(2 ; −3) ─────────────────────────
const SVG_PLACER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 322" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Question : où placer M', symétrique de M par le demi-tour de centre O ? Sur le quadrillage, M est 2 carreaux à gauche et 3 carreaux en haut de O. En 3 étapes à faire défiler avec les flèches. Étape 1 : la figure avec O et M. Étape 2 : de M à O, on va 2 carreaux vers la droite et 3 vers le bas. Étape 3 : à partir de O, on refait exactement le même déplacement, 2 vers la droite et 3 vers le bas, et on arrive sur M'. Vérification : M, O et M' sont alignés et O est au milieu. Réponse : M' est 2 carreaux à droite et 3 carreaux en bas de O">
  <style>
    @keyframes cp1119-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1119-pop { opacity: 0; animation: cp1119-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Repère O et M sur le quadrillage</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Le déplacement de M à O</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Le même déplacement à partir de O</text>

  <!-- ── La figure (étape 1) ─────────────────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <text x="8" y="44" font-size="11" font-weight="700" fill="#1e293b">Les points O et M sont placés sur le quadrillage.</text>
    <text x="8" y="60" font-size="11" font-weight="700" fill="#1E3A8A">Question : où placer M', symétrique de M</text>
    <text x="8" y="74" font-size="11" font-weight="700" fill="#1E3A8A">par le demi-tour de centre O ?</text>
    ${GRILLE}
    ${POINT(0, 0, 'O', '#b45309')}
    ${POINT(-2, 3, 'M', '#1E3A8A')}
    <text x="182" y="120" font-size="11" font-weight="700" fill="#1E3A8A">O est le centre.</text>
    <text x="182" y="142" font-size="11" font-weight="700" fill="#1E3A8A">M' doit être de l'autre</text>
    <text x="182" y="156" font-size="11" font-weight="700" fill="#1E3A8A">côté de O, à la même</text>
    <text x="182" y="170" font-size="11" font-weight="700" fill="#1E3A8A">distance : O sera le</text>
    <text x="182" y="184" font-size="11" font-weight="700" fill="#1E3A8A">milieu de M et M'.</text>
  </g>

  <!-- ── De M à O (étape 2) ─────────────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1119-pop">
      ${FLECHE(-2, 3, 0, 0, '#1D9E75')}
      <text x="182" y="212" font-size="11" font-weight="700" fill="#1D9E75">De M à O :</text>
      <text x="182" y="226" font-size="11" font-weight="700" fill="#1D9E75">2 carreaux vers la droite,</text>
      <text x="182" y="240" font-size="11" font-weight="700" fill="#1D9E75">3 carreaux vers le bas.</text>
    </g>
  </g>

  <!-- ── De O à M' (étape 3) ────────────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1119-pop">
      ${FLECHE(0, 0, 2, -3, '#B45309')}
      ${POINT(2, -3, "M'", '#B45309', 7, 14)}
      <text x="182" y="264" font-size="11" font-weight="700" fill="#B45309">De O à M' : le même</text>
      <text x="182" y="278" font-size="11" font-weight="700" fill="#B45309">déplacement, 2 à droite</text>
      <text x="182" y="292" font-size="11" font-weight="700" fill="#B45309">et 3 en bas. ✓</text>
      <text x="8" y="314" font-size="11.5" font-weight="700" fill="#1e293b">Réponse : M' est 2 carreaux à droite et 3 carreaux en bas de O.</text>
    </g>
  </g>
</svg>`.trim();

const AIDE_PLACER = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Le symétrique de M par le demi-tour de centre O** est le point M' tel que O soit le milieu du segment [MM'] : M' est de l'autre côté de O, à la même distance.",
                "**Sur le quadrillage** : compte le déplacement qui va de M à O (par exemple 3 carreaux vers la droite et 1 vers le bas), puis refais exactement le même déplacement à partir de O. Tu arrives sur M'.",
                "**Vérifie** : M, O et M' sont alignés, et M' est aussi loin de O que M, de l'autre côté.",
                "**Attention, ce n'est pas un pliage** : si tu n'inverses qu'une seule direction (gauche-droite ou haut-bas), tu obtiens une symétrie axiale, pas un demi-tour. Les deux directions s'inversent.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_PLACER },
    ],
};

// ─── V/F : O(1 ; 0), M(−2 ; 2), N(4 ; −2) ──────────────────────────────────
const SVG_VF = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 322" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Question : N est-il le symétrique de M par le demi-tour de centre O ? Sur le quadrillage, M est 3 carreaux à gauche et 2 en haut de O, N est 3 carreaux à droite et 2 en bas de O. En 3 étapes à faire défiler avec les flèches. Étape 1 : la figure avec O, M et N. Étape 2 : de M à O, 3 carreaux vers la droite et 2 vers le bas ; de O à N, 3 vers la droite et 2 vers le bas, le même déplacement. Étape 3 : donc N est le symétrique de M ; M, O et N sont alignés, N est à la même distance de O que M, et N n'est pas du même côté que M. Réponse : N est le symétrique de M par le demi-tour de centre O">
  <style>
    @keyframes cp1119-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1119-pop { opacity: 0; animation: cp1119-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Repère O, M et N sur le quadrillage</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Compare les deux déplacements</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Conclus, puis relis chaque affirmation</text>

  <!-- ── La figure (étape 1) ─────────────────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <text x="8" y="44" font-size="11" font-weight="700" fill="#1e293b">Les points O, M et N sont placés sur le quadrillage.</text>
    <text x="8" y="60" font-size="11" font-weight="700" fill="#1E3A8A">Question : N est-il le symétrique de M</text>
    <text x="8" y="74" font-size="11" font-weight="700" fill="#1E3A8A">par le demi-tour de centre O ?</text>
    ${GRILLE}
    ${POINT(1, 0, 'O', '#b45309')}
    ${POINT(-2, 2, 'M', '#1E3A8A')}
    ${POINT(4, -2, 'N', '#1E3A8A', 7, 14)}
    <text x="182" y="120" font-size="11" font-weight="700" fill="#1E3A8A">Si N est le symétrique,</text>
    <text x="182" y="134" font-size="11" font-weight="700" fill="#1E3A8A">O est au milieu de M et N :</text>
    <text x="182" y="148" font-size="11" font-weight="700" fill="#1E3A8A">le déplacement de M à O</text>
    <text x="182" y="162" font-size="11" font-weight="700" fill="#1E3A8A">et celui de O à N sont</text>
    <text x="182" y="176" font-size="11" font-weight="700" fill="#1E3A8A">les mêmes.</text>
  </g>

  <!-- ── Les deux déplacements (étape 2) ─────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1119-pop">
      ${FLECHE(-2, 2, 1, 0, '#1D9E75')}
      ${FLECHE(1, 0, 4, -2, '#1D9E75')}
      <text x="182" y="204" font-size="11" font-weight="700" fill="#1D9E75">De M à O : 3 à droite, 2 en bas.</text>
      <text x="182" y="218" font-size="11" font-weight="700" fill="#1D9E75">De O à N : 3 à droite, 2 en bas.</text>
      <text x="182" y="232" font-size="11" font-weight="700" fill="#1D9E75">Le même déplacement. ✓</text>
    </g>
  </g>

  <!-- ── Conclusion et relecture des affirmations (étape 3) ──────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1119-pop">
      <text x="182" y="256" font-size="11" font-weight="700" fill="#B45309">M, O et N alignés : vrai.</text>
      <text x="182" y="270" font-size="11" font-weight="700" fill="#B45309">N aussi loin de O que M : vrai.</text>
      <text x="182" y="284" font-size="11" font-weight="700" fill="#B45309">N du même côté que M : faux.</text>
      <text x="8" y="314" font-size="11.5" font-weight="700" fill="#1e293b">Réponse : N est le symétrique de M par le demi-tour de centre O.</text>
    </g>
  </g>
</svg>`.trim();

const AIDE_VF = {
    pages: [
        {
            titre: 'La méthode',
            lignes: [
                "**Pour savoir si N est le symétrique de M par le demi-tour de centre O**, compare deux déplacements sur le quadrillage : celui qui va de M à O et celui qui va de O à N. S'ils sont identiques, O est le milieu de [MN] et N est le symétrique.",
                "**Trois contrôles vont ensemble** quand N est le symétrique : M, O et N sont alignés ; N est aussi loin de O que M ; N est de l'autre côté de O. Il faut les trois.",
                "**Même distance mais pas alignés** : ce n'est pas un demi-tour. C'est une symétrie par rapport à une droite (l'une des deux directions seulement est inversée) ou un quart de tour.",
                "**Du même côté de O que M** : ce n'est jamais le symétrique par le demi-tour, même à la même distance.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_VF },
    ],
};

const CP_1119 = {
    placer_placer_points: AIDE_PLACER,
    placer_qcm_unique: AIDE_PLACER,
    vf_tableau_vf: AIDE_VF,
};

export default CP_1119;
